import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { track } from "@/api/analytics";
import { useSettings } from "@/context/SettingsContext";
import ApartmentCard from "@/components/ApartmentCard";
import InfoTile from "@/components/InfoTile";
import WeatherWidget from "@/components/WeatherWidget";
import Logo from "@/components/Logo";
import { pickLocale } from "@/lib/helpers";
import { Loader2 } from "lucide-react";

export default function Home() {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const [apartments, setApartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    track("view_home");
    Promise.all([api.get("/public/apartments"), api.get("/public/categories")])
      .then(([a, c]) => {
        setApartments(a.data || []);
        setCategories(c.data || []);
      })
      .catch(() => {
        // Public endpoints occasionally 404/timeout on cold-start; keep the
        // Home page rendered with an empty state instead of surfacing an
        // uncaught Axios error to the guest.
      })
      .finally(() => setLoading(false));
  }, []);

  const tagline = pickLocale(settings?.tagline, i18n.language) || t("home.welcome_heading");
  const welcome = pickLocale(settings?.welcome_text, i18n.language) || t("home.welcome_text");

  return (
    <div className="px-6">
      {/* Hero */}
      <section className="pt-8 pb-4 text-center animate-fade-up">
        <div className="flex justify-center">
          <Logo variant="full" className="w-[144px] h-[144px]" />
        </div>
        <h1
          data-testid="home-title"
          className="font-editorial italic text-[40px] leading-[1.05] text-ink-900 mt-1"
        >
          {tagline}
        </h1>
        <div className="flex justify-center my-4">
          <span className="ornament" />
        </div>
        <div className="text-[11px] font-semibold tracking-overline text-bronze-500">
          {settings?.location_label || "SIRMIONE · LAGO DI GARDA"}
        </div>
        <p
          data-testid="home-tagline"
          className="font-editorial italic text-lg text-ink-500 mt-3"
        >
          {t("tagline.home")}
        </p>
        <p className="mt-4 text-[15px] text-ink-500 leading-[1.65] max-w-[400px] mx-auto">
          {welcome}
        </p>
      </section>

      {/* Weather */}
      <section className="mb-8 animate-fade-up" style={{ animationDelay: "80ms" }}>
        <WeatherWidget />
      </section>

      {/* Apartments */}
      <section className="animate-fade-up" style={{ animationDelay: "160ms" }} data-testid="home-apartments">
        <div className="flex flex-col items-center text-center mb-4">
          <span className="ornament mb-2" />
          <h2 className="text-[11px] font-semibold tracking-overline text-bronze-500">
            {t("home.apartments_title")}
          </h2>
        </div>
        <div className="flex flex-col gap-2.5">
          {loading && (
            <div className="flex items-center gap-2 text-ink-300 text-sm py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading")}
            </div>
          )}
          {!loading && apartments.map((apt) => <ApartmentCard key={apt.id} apartment={apt} />)}
        </div>
      </section>

      {/* Info Utili */}
      {categories.length > 0 && (
        <section className="mt-12 animate-fade-up" style={{ animationDelay: "240ms" }} data-testid="home-categories">
          <div className="flex flex-col items-center text-center mb-5">
            <span className="ornament mb-2" />
            <h2 className="text-[11px] font-semibold tracking-overline text-bronze-500">
              {t("home.info_title")}
            </h2>
            <p className="text-xs text-ink-300 mt-1.5">{t("home.info_subtitle")}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2.5" data-testid="info-grid">
            {categories.map((c) => (
              <div key={c.id} className="basis-[calc(33.333%-0.5rem)] max-w-[calc(33.333%-0.5rem)]">
                <InfoTile category={c} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="mt-16 mb-8 text-center text-[11px] text-ink-300 leading-relaxed">
        <div className="flex justify-center mb-3">
          <span className="ornament" />
        </div>
        <p className="tracking-wider">© {new Date().getFullYear()} · {settings?.site_name || "Appartamenti La Piazzetta"}</p>
      </footer>
    </div>
  );
}
